import { InputType } from "~/scad-shared/types/generic";
import type { IFieldConfig } from "../types/scad";
import { defaultColSpan, fullWidthColSpan, halfWidthColSpan } from "./defaults";

// ActionExecutionLocationEnum: Client = 0, Server = 1
export const executionLocationOptions = [
  { label: "Client", value: "Client" },
  { label: "Server", value: "Server" },
];

export const methodOptions = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
  { label: "PUT", value: "PUT" },
  { label: "PATCH", value: "PATCH" },
  { label: "DELETE", value: "DELETE" },
];

// ServerActionTypeEnum: None = 0, REST = 1, SQLSP = 2, SQLStatement = 3, GRAPHQL = 4
export const serverActionTypeOptions = [
  { label: "None", value: "None" },
  { label: "REST", value: "REST" },
  { label: "SQL Stored Procedure", value: "SQLSP" },
  { label: "SQL Statement", value: "SQLStatement" },
  { label: "GraphQL", value: "GRAPHQL" },
];

// ActionContentTypeEnum: JSON = 0, XML = 1, RAW = 2, BASE64 = 3, FORM = 4, HTML = 5, PDF = 6, Excel = 7, Word = 8, PNG = 9, JPEG = 10, GIF = 11, SVG = 12, CSV = 13, Text = 14
export const contentTypeOptions = [
  { label: "JSON", value: "JSON" },
  { label: "XML", value: "XML" },
  { label: "Raw", value: "RAW" },
  { label: "Base64", value: "BASE64" },
  { label: "Form", value: "FORM" },
  { label: "HTML", value: "HTML" },
  { label: "PDF", value: "PDF" },
  { label: "Excel", value: "Excel" },
  { label: "Word", value: "Word" },
  { label: "PNG", value: "PNG" },
  { label: "JPEG", value: "JPEG" },
  { label: "GIF", value: "GIF" },
  { label: "SVG", value: "SVG" },
  { label: "CSV", value: "CSV" },
  { label: "Text", value: "Text" },
];

// AuthenticationTypeEnum: None = 0, Basic = 1, Bearer = 2, AnotherAction = 3
export const authenticationTypeOptions = [
  { label: "None", value: "None" },
  { label: "Basic", value: "Basic" },
  { label: "Bearer", value: "Bearer" },
  { label: "Another Action", value: "AnotherAction" },
];

// PostActionResponseEnum: None = 0, Navigate = 1, Dialog = 2, SetPropertyValue = 3, Toast = 4, FileDownload = 5, NewTab = 6
export const postActionResponseOptions = [
  { label: "None", value: "None" },
  { label: "Navigate", value: "Navigate" },
  { label: "Dialog", value: "Dialog" },
  { label: "Set Property Value", value: "SetPropertyValue" },
  { label: "Toast", value: "Toast" },
  { label: "File Download", value: "FileDownload" },
  { label: "New Tab", value: "NewTab" },
];

export const filterQueryTypeOptions = [
  { label: "Default", value: "" },
  { label: "Serialized", value: "SERIALIZED" },
  { label: "Both", value: "BOTH" },
  { label: "Append Queries", value: "APPENDQUERIES" },
];

export const metaActionFieldConfig: IFieldConfig[] = [
  // Basic Info Group
  {
    name: "Name",
    label: "Action Name",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: halfWidthColSpan,
    required: true,
    validation: ["required"],
  },
  {
    name: "SortOrder",
    label: "Sort Order",
    inputType: InputType.IntegerInput,
    fieldGroup: "Basic Info",
    colSpan: halfWidthColSpan,
  },
  {
    name: "CssClass",
    label: "CSS Class",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: halfWidthColSpan,
  },
  {
    name: "ButtonImageCss",
    label: "Button Icon CSS",
    inputType: InputType.InputText,
    fieldGroup: "Basic Info",
    colSpan: halfWidthColSpan,
    help: "e.g., pi pi-check, pi pi-download",
  },

  // Execution Settings Group
  {
    name: "ExecutionLocation",
    label: "Execution Location",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Execution Settings",
    colSpan: halfWidthColSpan,
    required: true,
    validation: ["required"],
  },
  {
    name: "Method",
    label: "HTTP Method",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Execution Settings",
    colSpan: halfWidthColSpan,
    required: true,
    validation: ["required"],
  },
  {
    name: "Route",
    label: "Route/Endpoint",
    inputType: InputType.TextAreaInput,
    fieldGroup: "Execution Settings",
    colSpan: fullWidthColSpan,
    required: true,
    validation: ["required"],
    help: "e.g., /api/records/{Id}/approve",
  },
  {
    name: "ServerActionType",
    label: "Server Action Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Execution Settings",
    colSpan: halfWidthColSpan,
  },
  {
    name: "ContentType",
    label: "Content Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Execution Settings",
    colSpan: halfWidthColSpan,
  },
  {
    name: "FilterQueryType",
    label: "Filter Query Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Execution Settings",
    colSpan: halfWidthColSpan,
  },

  // Authentication Group
  {
    name: "AuthenticationType",
    label: "Authentication Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Authentication",
    colSpan: halfWidthColSpan,
  },
  {
    name: "AuthenticationToken",
    label: "Authentication Token",
    inputType: InputType.InputText,
    fieldGroup: "Authentication",
    colSpan: halfWidthColSpan,
  },
  {
    name: "AuthenticationActionId",
    label: "Auth Action ID",
    inputType: InputType.IntegerInput,
    fieldGroup: "Authentication",
    colSpan: halfWidthColSpan,
  },
  {
    name: "AuthenticationActionResponseKey",
    label: "Auth Response Key",
    inputType: InputType.InputText,
    fieldGroup: "Authentication",
    colSpan: halfWidthColSpan,
  },

  // Response Settings Group
  {
    name: "PostActionResponse",
    label: "Post Action Response",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Response Settings",
    colSpan: halfWidthColSpan,
    required: true,
    validation: ["required"],
  },
  {
    name: "PostActionResponseKey",
    label: "Response Key",
    inputType: InputType.InputText,
    fieldGroup: "Response Settings",
    colSpan: halfWidthColSpan,
    help: "Key path to extract from response",
  },
  {
    name: "ResponseMessage",
    label: "Response Message",
    inputType: InputType.InputText,
    fieldGroup: "Response Settings",
    colSpan: fullWidthColSpan,
    help: "Message shown on success. Supports {field} placeholders",
  },
  {
    name: "ResponseContentType",
    label: "Response Content Type",
    inputType: InputType.SelectDropdown,
    fieldGroup: "Response Settings",
    colSpan: halfWidthColSpan,
  },

  // Display Settings Group
  {
    name: "GridAction",
    label: "Show in Grid",
    inputType: InputType.BoolSelect,
    fieldGroup: "Display Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "IsBulk",
    label: "Is Bulk Action",
    inputType: InputType.BoolSelect,
    fieldGroup: "Display Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "IsTopOfGrid",
    label: "Show at Top of Grid",
    inputType: InputType.BoolSelect,
    fieldGroup: "Display Settings",
    colSpan: defaultColSpan,
  },
  {
    name: "CanExecute",
    label: "Can Execute",
    inputType: InputType.BoolSelect,
    fieldGroup: "Display Settings",
    colSpan: defaultColSpan,
  },

  // Confirmation Dialog Group
  {
    name: "UseConfirmationDialog",
    label: "Use Confirmation Dialog",
    inputType: InputType.BoolSelect,
    fieldGroup: "Confirmation Dialog",
    colSpan: defaultColSpan,
  },
  {
    name: "ConfirmationHeaderText",
    label: "Confirmation Header",
    inputType: InputType.InputText,
    fieldGroup: "Confirmation Dialog",
    colSpan: halfWidthColSpan,
  },
  {
    name: "ConfirmationBodyText",
    label: "Confirmation Body",
    inputType: InputType.TextAreaInput,
    fieldGroup: "Confirmation Dialog",
    colSpan: fullWidthColSpan,
  },

  // Expressions Group
  {
    name: "ShowExpression",
    label: "Show Expression",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "Expression to determine visibility, e.g., Status == 'Active'",
  },
  {
    name: "DisabledExpression",
    label: "Disabled Expression",
    inputType: InputType.InputText,
    fieldGroup: "Expressions",
    colSpan: fullWidthColSpan,
    help: "Expression to determine if action is disabled",
  },

  // Cache Settings Group
  {
    name: "CacheResults",
    label: "Cache Results",
    inputType: InputType.BoolSelect,
    fieldGroup: "Cache Settings",
    colSpan: halfWidthColSpan,
  },
  {
    name: "CacheDuration",
    label: "Cache Duration (seconds)",
    inputType: InputType.IntegerInput,
    fieldGroup: "Cache Settings",
    colSpan: halfWidthColSpan,
  },

  // Advanced Group
  {
    name: "Body",
    label: "Request Body (JSON)",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: fullWidthColSpan,
    help: "Static JSON body to include in request",
  },
  {
    name: "GraphqlOperationName",
    label: "GraphQL Operation Name",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: halfWidthColSpan,
  },
  {
    name: "SQLConnectionName",
    label: "SQL Connection Name",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: halfWidthColSpan,
  },
  {
    name: "Permisssion",
    label: "Permission",
    inputType: InputType.InputText,
    fieldGroup: "Advanced",
    colSpan: halfWidthColSpan,
  },
];

// Group fields by fieldGroup for easier rendering
export function getMetaActionFieldsByGroup(): Map<string, IFieldConfig[]> {
  const groups = new Map<string, IFieldConfig[]>();

  metaActionFieldConfig.forEach((field) => {
    const groupName = field.fieldGroup || "Other";
    if (!groups.has(groupName)) {
      groups.set(groupName, []);
    }
    groups.get(groupName)!.push(field);
  });

  return groups;
}

// Get dropdown options for specific fields
export function getActionFieldOptions(
  fieldName: string,
): { label: string; value: any }[] | undefined {
  switch (fieldName) {
    case "ExecutionLocation":
      return executionLocationOptions;
    case "Method":
      return methodOptions;
    case "ServerActionType":
      return serverActionTypeOptions;
    case "ContentType":
    case "ResponseContentType":
      return contentTypeOptions;
    case "AuthenticationType":
      return authenticationTypeOptions;
    case "PostActionResponse":
      return postActionResponseOptions;
    case "FilterQueryType":
      return filterQueryTypeOptions;
    default:
      return undefined;
  }
}

// Get default values for a new action
export function getDefaultActionValues(metaTableId: number): Partial<any> {
  return {
    MetaTableId: metaTableId,
    Name: "",
    ExecutionLocation: "Client",
    Method: "POST",
    Route: "",
    ServerActionType: "None",
    ContentType: "JSON",
    ResponseContentType: "JSON",
    AuthenticationType: "None",
    PostActionResponse: "Toast",
    GridAction: false,
    SortOrder: 0,
    IsBulk: false,
    IsTopOfGrid: false,
    CanExecute: false,
    UseConfirmationDialog: false,
    CacheResults: false,
    CacheDuration: 0,
  };
}
